import { Injectable } from '@angular/core'
import {
  AnnotationsPeopleProfilesClient,
  CreatePeopleProfile,
  DEFAULT_PAGINATION_LIMIT,
  DetailedProfile,
  MAX_PAGINATION_LIMIT,
  PaginatedPeopleProfiles,
  PeopleProfile,
  PeopleProfilesQueryParams,
  UpdatePeopleProfile,
} from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, RemoteData, switchMapData } from '@ftr/foundation'
import { AnnotationsService } from '../base'

// limit to 10 calls x 100 records to prevent infinite loop in case of any issues
const MAX_REQUESTED_PAGES = 10

@Injectable({
  providedIn: 'root',
})
export class PeopleProfilesService extends AnnotationsService {
  getPeopleProfileForUser(
    courtSystemId: Uuid,
    involvement: PeopleProfile['involvement'],
  ): ApiResult<PeopleProfile | null> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['getPeopleProfileForUser']>>,
      PeopleProfile | null
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.getPeopleProfileForUser({ query: { courtSystemId, involvement } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.success(null)
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  getPeopleProfile(id: Uuid, courtSystemId: Uuid): ApiResult<PeopleProfile | null> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['getPeopleProfile']>>,
      PeopleProfile | null
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.getPeopleProfile({ params: { profileId: id } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch profile'))
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  /**
   * @param options
   * @returns People profiles within a court system. Empty paginated result if feature flag is off
   */
  getPeopleProfiles(
    options: Partial<PeopleProfilesQueryParams> & Pick<PeopleProfilesQueryParams, 'courtSystemId'>,
  ): ApiResult<PaginatedPeopleProfiles> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['listPeopleProfiles']>>,
      PaginatedPeopleProfiles
    >({
      courtSystemId: options.courtSystemId,
      requestFn: client =>
        client.peopleProfiles.listPeopleProfiles({
          query: { offset: 0, limit: DEFAULT_PAGINATION_LIMIT, ...options },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch profiles'))
        }

        return RemoteData.success<PaginatedPeopleProfiles>(result.body)
      },
    })
  }

  /**
   * Fetches all people profiles across all pages for the given options, up to 10 calls x 100 records.
   * Automatically paginates using offset until all results are collected.
   * @returns All matching PeopleProfile items as a flat array
   */
  getAllPeopleProfiles(
    options: Omit<
      Partial<PeopleProfilesQueryParams> & Pick<PeopleProfilesQueryParams, 'courtSystemId'>,
      'offset' | 'limit'
    >,
  ): ApiResult<PeopleProfile[]> {
    return this.fetchAllProfilesFrom(options, 0, [])
  }

  private fetchAllProfilesFrom(
    options: Omit<
      Partial<PeopleProfilesQueryParams> & Pick<PeopleProfilesQueryParams, 'courtSystemId'>,
      'offset' | 'limit'
    >,
    offset: number,
    accumulated: PeopleProfile[],
    requested: number = 0,
  ): ApiResult<PeopleProfile[]> {
    return this.getPeopleProfiles({ ...options, offset, limit: MAX_PAGINATION_LIMIT }).pipe(
      switchMapData(result => {
        const allItems = [...accumulated, ...result.items]
        if (allItems.length < result.meta.totalItems && requested < MAX_REQUESTED_PAGES) {
          return this.fetchAllProfilesFrom(options, offset + MAX_PAGINATION_LIMIT, allItems, requested + 1)
        }
        return ApiResult.success(allItems)
      }),
    )
  }

  suggestPeopleProfiles(
    courtSystemId: Uuid,
    options: {
      searchTerm?: string
      caseId?: string
      caseReference?: string
      excludeHearingSectionId?: string
    },
  ): ApiResult<{
    items: Pick<PeopleProfile, 'id' | 'name' | 'personId' | 'email' | 'dateOfBirth'>[]
    meta: {
      suggestionSources: ('case' | 'search-term' | 'recent-assignments')[]
    }
  }> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['suggestPeopleProfiles']>>,
      {
        items: Pick<PeopleProfile, 'id' | 'name' | 'personId' | 'email' | 'dateOfBirth'>[]
        meta: {
          suggestionSources: ('case' | 'search-term' | 'recent-assignments')[]
        }
      }
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.suggestPeopleProfiles({ query: { courtSystemId, ...options } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to suggest profiles'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  createProfile(body: CreatePeopleProfile): ApiResult<Partial<PeopleProfile>> {
    const { courtSystemId } = body
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['createPeopleProfile']>>,
      PeopleProfile
    >({
      courtSystemId,
      requestFn: client =>
        client.peopleProfiles.createPeopleProfile({
          body,
        }),
      responseMapFn: result => {
        if (result.status !== 201) {
          return RemoteData.failure(new Error('Failed to create profiles'))
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  updateProfile(profileId: Uuid, body: UpdatePeopleProfile, courtSystemId: Uuid): ApiResult<Partial<PeopleProfile>> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['updatePeopleProfile']>>,
      PeopleProfile
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.updatePeopleProfile({ body, params: { profileId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update profile'))
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  deleteProfile({ profileId, courtSystemId }: { profileId: Uuid; courtSystemId: Uuid }): ApiResult {
    return this.performApiRequest<Awaited<ReturnType<AnnotationsPeopleProfilesClient['deletePeopleProfile']>>, null>({
      courtSystemId,
      requestFn: client =>
        client.peopleProfiles.deletePeopleProfile({ params: { profileId }, query: { courtSystemId } }),
      responseMapFn: result => {
        if (result.status !== 204) {
          return RemoteData.failure(new Error('Failed to delete profile'))
        }

        return RemoteData.success<null>(null)
      },
    })
  }

  /**
   * Get detailed profile including associated cases, recordings, and live recording
   * @param profileId - The UUID of the profile to fetch
   * @param courtSystemId - The UUID of the court system
   * @returns Detailed profile with cases, recordings, and live recording data
   */
  getDetailedProfile(profileId: Uuid, courtSystemId: Uuid): ApiResult<DetailedProfile> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['getDetailedProfile']>>,
      DetailedProfile
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.getDetailedProfile({ params: { profileId } }),
      // 404 and other non-200 responses must be failure so consumers (e.g. judge details + ftr-remote) show error state instead of hanging on loading (EN-1178).
      responseMapFn: result =>
        result.status === 200
          ? RemoteData.success(result.body)
          : RemoteData.failure(new Error('Failed to fetch detailed profile')),
    })
  }
}
